import { Bot, MessageCircle, Send, User, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { getAIResponse } from "../../api/common";
import "./chatBot.css";

interface Message {
  id: number;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "Hi! I'm your AI assistant. How can I help you today?",
      isBot: true,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const simulateBotResponse = (userMessage: string) => {
    setIsTyping(true);

    setTimeout(
      () => {
        const request = { userMessage };

        getAIResponse(request).then((response) => {
          const newMessage: Message = {
            id: Date.now(),
            text: response,
            isBot: true,
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, newMessage]);
          setIsTyping(false);
        });
      },
      1000 + (crypto.getRandomValues(new Uint32Array(1))[0] % 2001)
    );
  };

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now(),
      text: inputText,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    simulateBotResponse(inputText);
    setInputText("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      {/* Chat Window */}
      <div className={`chatbot-container ${isOpen ? "open" : ""}`} data-testid="chatbot-container">
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar">
              <Bot size={24} />
            </div>
            <div>
              <h3 className="chatbot-title" data-testid="chatbot-title">
                AI Assistant
              </h3>
              <p className="chatbot-status">Online now</p>
            </div>
          </div>
          <button
            data-testid="chatbot-close-button"
            onClick={() => setIsOpen(false)}
            className="chatbot-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((message) => {
            const messageIsBot = message.isBot ? "bot" : "user";
            return (
              <div key={message.id} className={`message ${messageIsBot}`}>
                {message.isBot && (
                  <div className="message-avatar bot">
                    <Bot size={16} />
                  </div>
                )}
                <div className={`message-content ${messageIsBot}`}>
                  <p
                    className="message-text"
                    dangerouslySetInnerHTML={{ __html: message.text }}
                  />
                  <p className={`message-time ${messageIsBot}`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
                {!message.isBot && (
                  <div className="message-avatar user">
                    <User size={16} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className="typing-indicator" data-testid="typing-indicator">
              <div className="message-avatar bot">
                <Bot size={16} />
              </div>
              <div className="typing-dots">
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chatbot-input-area">
          <div className="chatbot-input-container">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="chatbot-input"
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim()}
              className="chatbot-send-btn"
              data-testid="chatbot-send-button"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Floating Chat Button */}
      <button onClick={() => setIsOpen(!isOpen)} className="chatbot-float-btn" data-testid="chatbot-float-button">
        <div className="pulse-ring"></div>
        <MessageCircle
          size={28}
          className={`icon ${isOpen ? "hidden" : "visible"}`}
        />
        <X size={28} className={`icon ${isOpen ? "visible" : "hidden"}`} />

        {/* Notification dot */}
        <div className="notification-badge"></div>
      </button>

      {/* Overlay */}
      <div
        className={`chatbot-overlay ${isOpen ? "visible" : ""}`}
        onClick={() => setIsOpen(false)}
        data-testid="chatbot-open-button"
        aria-label="Close chatbot"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(false);
          }
        }}
      />
    </>
  );
};

export default ChatBot;
